import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@veil/ui/components/button";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import { useState } from "react";
import { toast } from "sonner";
import { TlsCertDownload } from "@/components/tls-cert-download";
import {
	ACME_COPY,
	type IssuanceCopy,
	ORIGIN_CA_COPY,
	postTls,
	type TlsMode,
} from "../tls-api";
import { TlsModeSelect, tlsModeLabel, useTlsForm } from "../tls-controls";
import { SETUP_STATUS_KEY } from "../use-setup-status";
import { IssuanceProgress } from "./issuance-progress";

/**
 * Step 3: TLS mode. The three top-level modes render as an intact radio
 * group; the selected branch's configuration (Cloudflare Flexible/Full,
 * origin cert source, mTLS, BYO PEMs, ACME email) expands in a single panel
 * below the group. Validation is react-hook-form + zod, surfaced inline on
 * submit. Flows that issue a certificate — Automatic HTTPS and Cloudflare
 * Full + Origin CA — stream their issuance Operation inline.
 */
export function TlsStep({
	initialMode,
	hostname,
	evaluation,
	onNext,
	onBack,
}: {
	initialMode: TlsMode;
	hostname: string | null;
	evaluation?: boolean;
	onNext: () => void;
	onBack: () => void;
}) {
	const queryClient = useQueryClient();
	// Without a public hostname (or in IP-only evaluation) the edge can't
	// present a real vhost/origin cert, so acme / byo / cloudflare-full are
	// unavailable — fall back to Cloudflare Flexible.
	const hostnameAvailable = !!hostname && !evaluation;
	const startMode: TlsMode =
		!hostnameAvailable && initialMode !== "cloudflare"
			? "cloudflare"
			: initialMode;

	const { form, value, willIssue, buildRequest } = useTlsForm({
		mode: startMode,
	});
	const [operationId, setOperationId] = useState<string | null>(null);
	const [operationCopy, setOperationCopy] = useState<IssuanceCopy>(ACME_COPY);
	const [opStatus, setOpStatus] = useState("");

	const invalidateSummary = () => {
		queryClient.invalidateQueries({ queryKey: SETUP_STATUS_KEY });
		queryClient.invalidateQueries({ queryKey: ["cluster"] });
	};

	const save = form.handleSubmit(async () => {
		setOpStatus("");
		try {
			const res = await postTls(buildRequest());
			invalidateSummary();
			if (res.operationId) {
				setOperationCopy(value.mode === "acme" ? ACME_COPY : ORIGIN_CA_COPY);
				setOperationId(res.operationId);
			} else {
				toast.success(`TLS set to ${tlsModeLabel(value)}`);
				onNext();
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Could not set TLS");
		}
	});

	// Retry: kick a fresh issuance Operation for the same configuration.
	const retry = () => {
		setOperationId(null);
		void save();
	};

	const saving = form.formState.isSubmitting;
	const succeeded = opStatus === "succeeded";
	const issuing = !!operationId && !succeeded && opStatus !== "failed";

	// Cert download is meaningful whenever an origin cert exists — i.e. every
	// mode except Cloudflare Flexible (which has no origin cert).
	const hasOriginCert = !(
		value.mode === "cloudflare" && value.cfMode === "flexible"
	);

	return (
		<form onSubmit={save} noValidate>
			<TlsModeSelect
				form={form}
				hostname={hostname}
				hostnameAvailable={hostnameAvailable}
				disabled={issuing}
			/>

			{!operationId && hasOriginCert && (
				<div className="mt-4">
					<TlsCertDownload enabled={hasOriginCert} />
				</div>
			)}

			{operationId && (
				<IssuanceProgress
					operationId={operationId}
					hostname={hostname}
					copy={operationCopy}
					onStatusChange={setOpStatus}
					onRetry={retry}
					retrying={saving}
				/>
			)}

			<div className="mt-6 flex items-center justify-between gap-2">
				<Button
					type="button"
					variant="ghost"
					onClick={onBack}
					disabled={saving || issuing}
				>
					Back
				</Button>
				{operationId ? (
					<Button
						type="button"
						onClick={onNext}
						disabled={!succeeded}
						className="h-11"
					>
						Continue
					</Button>
				) : (
					<Button type="submit" disabled={saving} className="h-11">
						{saving && (
							<Loader2 data-icon="inline-start" className="animate-spin" />
						)}
						{willIssue ? "Save & issue certificate" : "Save & continue"}
					</Button>
				)}
			</div>
		</form>
	);
}
